import { DAEMON_URL } from "../config";

async function post<T>(path: string, body: unknown): Promise<{ status: number; data: T }> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  const text = await res.text();
  let data: unknown;
  try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
  return { status: res.status, data: data as T };
}

export type RegisterResponse = {
  userId: number;
  email: string;
  role: "admin" | "member";
  totpProvisioningUri: string;
  totpSecret: string;
  /** Ticket for the enrolment call. `userId` is still sent but inert for it. */
  enrollToken: string;
};

export type LoginInitiateResponse = {
  requires2fa?: boolean;
  requirePasswordChange?: boolean;
  /**
   * Short-lived proof that the password step actually happened: 5 minutes, 5
   * code attempts. It replaces the userId this endpoint used to hand back.
   * A userId is enumerable, and `/auth/login/2fa` accepting one meant the code
   * step could be called on its own and still mint a session — the password
   * was never checked.
   */
  twoFactorToken: string;
};
export type AuthedUser = {
  email: string;
  role: "admin" | "member";
  requirePasswordChange?: boolean;
};

export async function register(email: string, password: string): Promise<RegisterResponse> {
  const r = await post<RegisterResponse | { error: string }>("/auth/register", { email, password });
  if (r.status !== 200) throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  return r.data as RegisterResponse;
}

/** Enrolled, and the session is minted. */
export type EnrolOk = { kind: "ok" };
/** The ticket is dead — unknown, past 15 minutes, or out of its 5 attempts. */
export type EnrolExpired = { kind: "expired" };
/** The account already has a verified authenticator; enrolment is not the way in. */
export type EnrolAlreadyDone = { kind: "already_enrolled" };
export type EnrolResult = EnrolOk | EnrolExpired | EnrolAlreadyDone;

/**
 * The one enrolment mechanism, which the daemon serves at two URLs.
 *
 * `/auth/2fa/setup` and `/auth/invite/enroll-2fa` are the same handler with
 * the same body and the same four answers, so there is one implementation
 * here and two named exports rather than two copies that could drift.
 *
 * A wrong code throws, which keeps the caller on the code field with its
 * remaining attempts. The two fatal outcomes are returned instead, because
 * neither is exceptional and each needs a different way out: a dead ticket
 * means fetch a new one, an already-enrolled account means go and sign in.
 */
async function postEnrolment(path: string, enrollToken: string, code: string): Promise<EnrolResult> {
  const r = await post<{ error?: string }>(path, { enrollToken, code });
  if (r.status === 200) return { kind: "ok" };
  if (r.status === 404) return { kind: "expired" };
  if (r.status === 409) return { kind: "already_enrolled" };
  throw new Error((r.data as { error?: string }).error || `http ${r.status}`);
}

/**
 * Enrol via the bootstrap / first-login route, minting the session.
 *
 * Takes the ticket from `/auth/register` or `/auth/2fa/init`, never a userId.
 * This endpoint used to accept `{userId, code}` and check only that a secret
 * existed — never `totp_verified` — so any account that had ever enrolled,
 * admins included, could be signed into with a guessed id and a brute-forced
 * code, no password. The ticket is what closes that.
 */
export function setup2fa(enrollToken: string, code: string): Promise<EnrolResult> {
  return postEnrolment("/auth/2fa/setup", enrollToken, code);
}

export type LoginNeedsSetup = { kind: "needs_setup"; userId: number };
export type LoginNeeds2FA = { kind: "needs_2fa"; twoFactorToken: string };
export type LoginResult = LoginNeedsSetup | LoginNeeds2FA;

export async function login(email: string, password: string): Promise<LoginResult> {
  const r = await post<LoginInitiateResponse | { error: string; userId?: number }>("/auth/login", { email, password });
  if (r.status === 403) {
    const body = r.data as { error?: string; userId?: number };
    if (typeof body.userId === "number") return { kind: "needs_setup", userId: body.userId };
    throw new Error(body.error || `http 403`);
  }
  if (r.status !== 200) {
    const err = r.data as { error: string };
    throw new Error(err.error || `http ${r.status}`);
  }
  const data = r.data as LoginInitiateResponse;
  return { kind: "needs_2fa", twoFactorToken: data.twoFactorToken };
}

export type Init2faResponse = {
  userId: number;
  email: string;
  totpProvisioningUri: string;
  totpSecret: string;
  /** Ticket for the enrolment call. `userId` is still sent but inert for it. */
  enrollToken: string;
};

export async function init2fa(email: string, password: string): Promise<Init2faResponse> {
  const r = await post<Init2faResponse | { error: string }>("/auth/2fa/init", { email, password });
  if (r.status !== 200) throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  return r.data as Init2faResponse;
}

/** The code was accepted and the session is minted. */
export type Login2faOk = { kind: "ok"; user: AuthedUser };
/** The two-factor token is dead; no code will ever be accepted against it. */
export type Login2faExpired = { kind: "expired" };
export type Login2faResult = Login2faOk | Login2faExpired;

/**
 * Does this response mean the two-factor token itself is dead — unknown,
 * expired, or out of attempts — rather than the code simply being wrong?
 *
 * The difference decides where the user lands. A wrong code keeps them on the
 * code field with attempts left; a dead token has to send them back to the
 * password step, because nothing they type can succeed any more.
 *
 * The daemon pins this as `403 { code: "two_factor_token_expired" }`, with a
 * live token and a wrong code answering `401 { error: "invalid TOTP code" }`.
 * The one code covers unknown, expired and spent alike: the holder has already
 * proved the password, so splitting those buys nothing.
 *
 * The body marker is honoured independently of the status, so a daemon that
 * moved the signal off 403 would still be understood. Status 401 is
 * deliberately never fatal on its own — it is the retryable wrong-code answer,
 * and treating it as fatal would throw a user back a step over a typo.
 */
function isDeadTwoFactorToken(status: number, data: unknown): boolean {
  if (status === 403) return true;
  return (data as { code?: string } | null)?.code === "two_factor_token_expired";
}

/**
 * Exchange the two-factor token plus a code for a session.
 *
 * Sends no userId, by design: accepting one is what let the code step be
 * called without the password step. Do not add it back as a fallback for an
 * older daemon — that reopens the hole it closes.
 */
export async function login2fa(twoFactorToken: string, code: string): Promise<Login2faResult> {
  const r = await post<AuthedUser | { error: string; code?: string }>("/auth/login/2fa", {
    twoFactorToken,
    code,
  });
  if (r.status === 200) return { kind: "ok", user: r.data as AuthedUser };
  if (isDeadTwoFactorToken(r.status, r.data)) return { kind: "expired" };
  throw new Error((r.data as { error: string }).error || `http ${r.status}`);
}

/** What the invite landing page needs before it can show a password form. */
export type InvitePreview = {
  email: string;
  /** True when redeeming the link must also enrol an authenticator. */
  needsTotp: boolean;
};

/**
 * Validate a one-time invite token without consuming it.
 *
 * A POST carrying the token in the body, never a GET carrying it in the path:
 * a URL path is recorded by access logs, CDNs and APM agents, which would undo
 * the whole reason the token travels in the URL fragment.
 *
 * Returns null when the daemon rejects the link. Unknown, already-redeemed and
 * expired tokens all come back as the same 404 carrying the same message, so
 * null is the only thing a caller can ever learn — the endpoint is deliberately
 * not an oracle. A transport or server fault still throws, which is what lets
 * the page tell "this link is dead" apart from "the daemon did not answer".
 */
export async function checkInvite(token: string): Promise<InvitePreview | null> {
  const r = await post<{ valid?: boolean; email: string; needsTotp: boolean } | { error: string }>(
    "/auth/invite/check",
    { token },
  );
  if (r.status === 404) return null;
  if (r.status !== 200) throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  const j = r.data as { valid?: boolean; email: string; needsTotp: boolean };
  // The daemon signals a bad link with 404, so `valid` should never be false
  // on a 200. It is checked anyway: this call is the gate in front of a
  // password form, and a daemon that ever answered 200 with valid:false would
  // otherwise open that form on a dead token.
  if (j.valid === false) return null;
  return { email: j.email, needsTotp: j.needsTotp };
}

/** Redeemed, and the account still has to enrol an authenticator. */
export type InviteNeedsSetup = {
  kind: "needs_setup";
  email: string;
  /** Short-lived (15 min) credential for the enrol call. Never a bare userId. */
  enrollToken: string;
  totpProvisioningUri: string;
  totpSecret: string;
};
/** Redeemed on an account whose existing authenticator still guards it. */
export type InviteDone = { kind: "done"; email: string };
/** The link stopped working between loading the page and submitting it. */
export type InviteInvalid = { kind: "invalid" };
export type AcceptInviteResult = InviteNeedsSetup | InviteDone | InviteInvalid;

type AcceptInviteResponse =
  | { needsTotp: true; email: string; enrollToken: string; totpProvisioningUri: string; totpSecret: string }
  | { needsTotp: false; email: string };

/**
 * Consume the token and set the password the invitee chose.
 *
 * A 404 here is not exceptional: the link is single-use, so a second tab or a
 * re-issued invite can retire it between the page loading and the form being
 * submitted. It comes back as `invalid` rather than a thrown error so the page
 * renders the same dead end it would have shown on arrival. `needs_setup`
 * carries an enrol token that `enroll2fa` exchanges for a session.
 */
export async function acceptInvite(token: string, password: string): Promise<AcceptInviteResult> {
  const r = await post<AcceptInviteResponse | { error: string }>("/auth/invite/accept", { token, password });
  if (r.status === 404) return { kind: "invalid" };
  if (r.status !== 200) throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  const data = r.data as AcceptInviteResponse;
  if (!data.needsTotp) return { kind: "done", email: data.email };
  return {
    kind: "needs_setup",
    email: data.email,
    enrollToken: data.enrollToken,
    totpProvisioningUri: data.totpProvisioningUri,
    totpSecret: data.totpSecret,
  };
}

/**
 * Enrol an authenticator with the token `acceptInvite` handed back, minting
 * the session.
 *
 * Deliberately not `/auth/2fa/setup`: that route is unauthenticated and keyed
 * on a bare `userId`, which is enumerable, so a freshly invited account is not
 * routed through it. This token is single-purpose, expires in 15 minutes, and
 * allows five code attempts.
 *
 * NOTE the shape here is NOT the shape `login2fa` uses, and the two must not
 * be assumed to match. Enrolment separates its failures by status alone: 404
 * is the dead-credential answer, matching `/auth/invite/check`, so it carries
 * no marker field. The login flow instead answers 403 with a `code` marker,
 * because a bare 401 there already means a retryable wrong code. Each is
 * internally consistent; they are simply not the same contract.
 *
 * A dead ticket is not the end for the invitee: `acceptInvite` has already
 * saved their password, so they can sign in and finish enrolling through the
 * never-enrolled path.
 */
export function enroll2fa(enrollToken: string, code: string): Promise<EnrolResult> {
  return postEnrolment("/auth/invite/enroll-2fa", enrollToken, code);
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const r = await post<{ ok: true } | { error: string }>("/auth/change-password", {
    currentPassword,
    newPassword,
  });
  if (r.status !== 200) {
    throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  }
}

export async function logout(): Promise<void> {
  await post("/auth/logout", {});
}

export async function needsBootstrap(): Promise<boolean> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/auth/needs-bootstrap`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  if (!res.ok) throw new Error(`http ${res.status}`);
  const j = (await res.json()) as { needsBootstrap: boolean };
  return j.needsBootstrap;
}
